import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import PageWrapper from "@/components/layout/PageWrapper";

const servicesData = [
  {
    title: "services.card1Title",
    desc: "services.card1Desc",
    image: "/assets/images/2.jpeg",
  },
  {
    title: "services.card2Title",
    desc: "services.card2Desc",
    image: "/assets/images/11.jpg",
  },
  {
    title: "services.card3Title",
    desc: "services.card3Desc",
    image: "/assets/images/10.jpg",
  },
  {
    title: "services.card4Title",
    desc: "services.card4Desc",
    image: "/assets/images/15.jpg",
  },
  {
    title: "services.card5Title",
    desc: "services.card5Desc",
    image: "/assets/images/19.jpg",
  },
  {
    title: "services.card6Title",
    desc: "services.card6Desc",
    image: "/assets/images/6.jpeg",
  },
];

export default function Services() {
  const { t, i18n } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const isRTL = i18n.language?.startsWith("ar");
  const phoneNumber = "966532785554";
  const whatsappMessage = isRTL
    ? "مرحباً، أنا مهتم بخدمات ابراج الرفوف."
    : "Hello, I am interested in Arafouf services.";
  const whatsappLink = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <PageWrapper>
      {/* SEO */}
      <title>{t("meta.servicesTitle")}</title>
      <meta name="description" content={t("meta.servicesDesc")} />

      {/* Hero */}
      <section className="relative w-full min-h-[400px] md:min-h-[500px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <motion.img
            src="/assets/images/18.jpg"
            alt="Services"
            className="w-full h-full object-cover object-top"
            initial={{ scale: 1.08 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.4, ease: "easeOut" }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20" />
        </div>
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 pb-12 md:pb-20 pt-32 md:pt-40">
          <motion.h1
            className="text-4xl md:text-6xl font-bold text-white leading-tight mb-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {t("services.pageTitle")}
          </motion.h1>
          <motion.p
            className="text-lg md:text-xl text-white/80 max-w-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {t("services.pageDesc")}
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="w-full py-16 md:py-24 bg-[#F5F3EF]">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div ref={ref} className="space-y-10 md:space-y-12">
            <motion.div
              className="max-w-3xl"
              initial={{ opacity: 0, y: 26 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 26 }}
              transition={{ duration: 0.55 }}
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/10 text-brand px-4 py-2 text-xs md:text-sm font-semibold mb-4">
                <i className="ri-layout-grid-line" />
                {t("services.sectionTitle")}
              </span>
              <p className="text-base md:text-lg text-stone-600 leading-relaxed">
                {t("services.sectionDesc")}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {servicesData.map((service, index) => (
                <motion.div
                  key={index}
                  className="bg-white rounded-2xl border border-stone-200 overflow-hidden group shadow-[0_10px_35px_rgba(0,0,0,0.03)]"
                  initial={{ opacity: 0, y: 60 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
                  transition={{ duration: 0.6, delay: 0.08 * index }}
                  whileHover={{ y: -8 }}
                >
                  <div className="relative h-48 md:h-56 overflow-hidden">
                    <img
                      src={service.image}
                      alt={t(service.title)}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/35 to-transparent" />
                    <span className="absolute top-3 left-3 inline-flex items-center justify-center rounded-full bg-white/90 text-stone-900 h-8 min-w-8 px-2 text-xs font-bold">
                      {`0${index + 1}`}
                    </span>
                  </div>
                  <div className="p-6 md:p-8">
                    <h3 className="text-xl md:text-2xl font-bold text-stone-900 mb-3">
                      {t(service.title)}
                    </h3>
                    <p className="text-sm md:text-base text-stone-600 leading-relaxed mb-4">
                      {t(service.desc)}
                    </p>
                    <a
                      href="/contact-us"
                      className="inline-flex items-center gap-2 text-sm font-medium text-brand hover:text-brand-dark transition-colors cursor-pointer"
                    >
                      {t("services.contactButton")}
                      <i className={`ri-${isRTL ? "arrow-left-line" : "arrow-right-line"}`} />
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-full py-16 md:py-24 bg-brand-50">
        <div className="w-full px-4 md:px-8 lg:px-12 text-center">
          <motion.div
            className="relative max-w-3xl mx-auto rounded-3xl border border-brand/20 bg-white px-6 py-10 md:px-10 md:py-12 shadow-[0_15px_45px_rgba(0,0,0,0.04)] overflow-hidden"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.span
              className="absolute -top-8 -right-8 h-24 w-24 rounded-full bg-brand/10"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            />
            <span className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/10 text-brand px-4 py-2 text-xs md:text-sm font-semibold mb-4">
              <i className="ri-customer-service-2-line" />
              {t("services.talkServicesEyebrow")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">
              {t("services.talkToUs")}
            </h2>
            <p className="text-lg text-stone-600 mb-8">
              {t("services.talkDesc")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="/contact-us"
                className="inline-flex items-center gap-2 bg-brand text-white font-semibold px-8 py-4 rounded-full hover:bg-brand-dark transition-colors whitespace-nowrap cursor-pointer"
              >
                {t("services.contactButton")}
                <span className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full">
                  <i className={`${isRTL ? "ri-arrow-left-line" : "ri-arrow-right-line"} text-sm`} />
                </span>
              </a>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-6 py-4 rounded-full hover:bg-[#128C7E] transition-colors whitespace-nowrap cursor-pointer"
                aria-label="WhatsApp"
              >
                <i className="ri-whatsapp-line text-lg" />
                {t("services.whatsappButton")}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </PageWrapper>
  );
}